import React from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faLocationDot,
    faChargingStation,
    faPlug,
    faCircleCheck,
    faCircleXmark,
    faUser,
} from "@fortawesome/pro-regular-svg-icons";
import {
    StatusIconByKey,
    getStatusLabelWithMaintenance,
    getStatusColorStyle,
    getStatusStripeStyle,
    getStatusHexColor,
} from "../../helpers/StatusMapper";
import { SESSION_STATUSES, stripSiteWords } from "./chargeMonitorHelpers";

const STATUS_SURFACE_MIX = 12;

function useConnectorBits(item) {
    const { t } = useTranslation();
    const data = item?.Data || {};
    const status = (data.Status || data.chargingConnectorStatus || "").toUpperCase();
    const maintenanceMode = !!(data.maintenanceMode ?? data.MaintenanceMode);
    const visualStatus = maintenanceMode ? "UNAVAILABLE" : status;
    const isOnline = !!data.isOnline;
    const isSession = SESSION_STATUSES.includes(status);

    return {
        data,
        status,
        visualStatus,
        isOnline,
        isSession,
        siteName: data.ChargingSiteName || "–",
        chargerName: data.chargingPointName || stripSiteWords(data.ChargingSiteName, data.chargingPointName),
        connectorName: data.chargingConnectorName || stripSiteWords(data.ChargingSiteName, data.chargingConnectorName),
        chargerShort: stripSiteWords(data.ChargingSiteName, data.chargingPointName),
        connectorShort: stripSiteWords(data.ChargingSiteName, data.chargingConnectorName),
        statusLabel: getStatusLabelWithMaintenance(status, maintenanceMode, t),
        statusColorStyle: getStatusColorStyle(visualStatus),
        pointColorStyle: getStatusColorStyle(isOnline ? "ONLINE" : "OFFLINE"),
        pointLabel: isOnline ? t("common.online") : t("common.offline"),
        statusStripeStyle: getStatusStripeStyle(visualStatus),
        customerName: isSession ? data.SessionCustomerName : null,
    };
}

export function ChargeMonitorConnectorListRow({ item, onOpen }) {
    const bits = useConnectorBits(item);
    const chargingWho = bits.customerName || (bits.isSession ? bits.data.SessionTokenName : null);
    const statusTint = getStatusHexColor(bits.visualStatus);

    return (
        <button
            type="button"
            onClick={() => onOpen(item)}
            className="relative grid w-full grid-cols-12 items-center gap-x-2 overflow-hidden rounded-lg border border-mute-200 py-1.5 pl-4 pr-3 text-left text-sm font-medium hover:brightness-95"
            style={{
                backgroundColor: `color-mix(in srgb, ${statusTint} ${STATUS_SURFACE_MIX}%, white)`,
            }}
        >
            <span
                className="absolute inset-y-0 left-0 w-1"
                style={bits.statusStripeStyle}
            />
            <span className="col-span-3 flex min-w-0 items-center gap-1.5">
                <FontAwesomeIcon icon={faLocationDot} className="shrink-0 text-sky-500" />
                <span className="truncate" title={bits.siteName}>{bits.siteName}</span>
            </span>
            <span className="col-span-2 flex min-w-0 items-center gap-1.5">
                <FontAwesomeIcon icon={faChargingStation} className="shrink-0 text-kraft-500" />
                <span className="truncate" title={bits.chargerName}>{bits.chargerShort}</span>
            </span>
            <span className="col-span-2 flex min-w-0 items-center gap-1.5">
                <FontAwesomeIcon icon={faPlug} className="shrink-0 text-mute-500" />
                <span className="truncate" title={bits.connectorName}>{bits.connectorShort}</span>
            </span>
            <span className="col-span-2 flex min-w-0 items-center gap-1.5">
                {chargingWho ? (
                    <>
                        <FontAwesomeIcon icon={faUser} className="shrink-0 text-mute-500" />
                        <span className="truncate" title={chargingWho}>{chargingWho}</span>
                    </>
                ) : (
                    <span className="truncate">&nbsp;</span>
                )}
            </span>
            <span className="col-span-3 flex min-w-0 items-center justify-end">
                <span className="grid w-64 max-w-full grid-cols-[minmax(0,1fr)_auto] items-center gap-3">
                    <span className="flex min-w-0 items-center gap-1.5">
                        <FontAwesomeIcon
                            icon={StatusIconByKey[bits.visualStatus] ?? StatusIconByKey.DEFAULT}
                            className="shrink-0"
                            style={bits.statusColorStyle}
                        />
                        <span className="truncate" title={bits.statusLabel} style={bits.statusColorStyle}>
                            {bits.statusLabel}
                        </span>
                    </span>
                    <span className="flex w-24 items-center gap-1.5">
                        <FontAwesomeIcon
                            icon={bits.isOnline ? faCircleCheck : faCircleXmark}
                            className="shrink-0"
                            style={bits.pointColorStyle}
                        />
                        <span className="truncate" style={bits.pointColorStyle}>
                            {bits.pointLabel}
                        </span>
                    </span>
                </span>
            </span>
        </button>
    );
}

export function ChargeMonitorConnectorBox({ item, onOpen }) {
    const bits = useConnectorBits(item);
    const chargingWho = bits.customerName || (bits.isSession ? bits.data.SessionTokenName : null);
    const statusTint = getStatusHexColor(bits.visualStatus);

    return (
        <button
            type="button"
            onClick={() => onOpen(item)}
            className="relative flex w-full min-w-0 flex-col self-stretch overflow-hidden rounded-xl border border-mute-200 px-3 py-2.5 text-left shadow-md hover:brightness-95"
            style={{
                backgroundColor: `color-mix(in srgb, ${statusTint} ${STATUS_SURFACE_MIX}%, white)`,
            }}
        >
            <span
                className="absolute inset-y-0 left-0 w-1"
                style={bits.statusStripeStyle}
            />
            <span className="flex min-h-0 flex-1 flex-col gap-1 pl-2 text-sm">
                <span className="flex min-w-0 items-center gap-1.5">
                    <span className="flex size-5 shrink-0 items-center justify-center">
                        <FontAwesomeIcon icon={faLocationDot} className="text-sm text-sky-500" />
                    </span>
                    <span className="min-w-0 truncate font-medium tracking-normal text-black" title={bits.siteName}>
                        {bits.siteName}
                    </span>
                </span>
                <span className="flex min-w-0 items-center gap-1.5">
                    <span className="flex size-5 shrink-0 items-center justify-center">
                        <FontAwesomeIcon icon={faChargingStation} className="text-sm text-kraft-500" />
                    </span>
                    <span className="min-w-0 truncate font-medium tracking-normal text-black" title={bits.chargerName}>
                        {bits.chargerShort}
                    </span>
                </span>
                <span className="flex min-w-0 items-center gap-1.5">
                    <span className="flex size-5 shrink-0 items-center justify-center">
                        <FontAwesomeIcon icon={faPlug} className="text-sm text-mute-500" />
                    </span>
                    <span className="min-w-0 truncate font-medium tracking-normal text-black" title={bits.connectorName}>
                        {bits.connectorShort}
                    </span>
                </span>
                <span className="flex h-5 min-w-0 items-center gap-1.5">
                    {chargingWho ? (
                        <>
                            <span className="flex size-5 shrink-0 items-center justify-center">
                                <FontAwesomeIcon icon={faUser} className="text-sm text-mute-500" />
                            </span>
                            <span className="min-w-0 truncate font-medium tracking-normal text-black" title={chargingWho}>
                                {chargingWho}
                            </span>
                        </>
                    ) : null}
                </span>
                <span className="mt-auto flex h-5 min-w-0 items-center gap-2 pt-0.5">
                    <span className="flex min-w-0 flex-1 items-center gap-1.5">
                        <span className="flex size-5 shrink-0 items-center justify-center">
                            <FontAwesomeIcon
                                icon={StatusIconByKey[bits.visualStatus] ?? StatusIconByKey.DEFAULT}
                                className="text-sm"
                                style={bits.statusColorStyle}
                            />
                        </span>
                        <span className="truncate font-medium" style={bits.statusColorStyle} title={bits.statusLabel}>
                            {bits.statusLabel}
                        </span>
                    </span>
                    <span className="flex shrink-0 items-center gap-1.5">
                        <span className="flex size-5 shrink-0 items-center justify-center">
                            <FontAwesomeIcon
                                icon={bits.isOnline ? faCircleCheck : faCircleXmark}
                                className="text-sm"
                                style={bits.pointColorStyle}
                            />
                        </span>
                        <span className="font-medium" style={bits.pointColorStyle}>
                            {bits.pointLabel}
                        </span>
                    </span>
                </span>
            </span>
        </button>
    );
}
